import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PrivacyPolicyModel } from '../../models/privacy-policy.model';
import { PrivacyPolicyService } from '../../services/privacy-policy.service';
import { SSOService } from '../../services/sso.service';
import { normalizeRichTextHtml } from '../../shared/utils/rich-text-html.util';

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './privacy-policy.html',
  styleUrl: './privacy-policy.css'
})
export class PrivacyPolicyComponent implements OnInit {
  policy: PrivacyPolicyModel | null = null;
  loading = true;
  saving = false;
  hasScrolledToEnd = false;
  errorMessage = '';
  isAcceptanceMode = false;
  isLoggedIn = false;

  @ViewChild('policyContent') private policyContentRef?: ElementRef<HTMLElement>;
  private static readonly SCROLL_END_THRESHOLD = 16;

  private returnUrl = '/home';

  constructor(
    private privacyPolicyService: PrivacyPolicyService,
    private ssoService: SSOService,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isLoggedIn = this.ssoService.isAuthenticated();
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.isAcceptanceMode = !!returnUrl;
    this.returnUrl = returnUrl || '/home';

    if (!this.isAcceptanceMode) {
      this.loadPublicPolicy();
      return;
    }

    this.privacyPolicyService.getMyStatus().subscribe({
      next: status => {
        this.policy = status.currentPolicy ?? null;
        this.loading = false;

        if (status.hasAcceptedLatest) {
          this.router.navigateByUrl(this.returnUrl);
          return;
        }

        // Render the content now so we can measure it, then enable Agree when the
        // policy fits without a scrollbar (the scroll event would never fire otherwise).
        this.cdr.detectChanges();
        this.updateScrollRequirement();
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Unable to load the privacy policy. Please try again.';
      }
    });
  }

  private loadPublicPolicy(): void {
    this.privacyPolicyService.getCurrentPolicy().subscribe({
      next: policy => {
        this.policy = policy;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Unable to load the privacy policy. Please try again.';
      }
    });
  }

  get policyHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(normalizeRichTextHtml(this.policy?.contentHtml));
  }

  onPolicyScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = PrivacyPolicyComponent.SCROLL_END_THRESHOLD;
    this.hasScrolledToEnd = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;
  }

  private updateScrollRequirement(): void {
    const element = this.policyContentRef?.nativeElement;
    if (!element) {
      return;
    }

    // No real overflow → nothing to scroll → allow agreeing immediately.
    if (element.scrollHeight <= element.clientHeight + PrivacyPolicyComponent.SCROLL_END_THRESHOLD) {
      this.hasScrolledToEnd = true;
    }
  }

  accept(): void {
    if (!this.isAcceptanceMode || !this.hasScrolledToEnd || this.saving) {
      return;
    }

    this.saving = true;
    this.privacyPolicyService.accept().subscribe({
      next: () => {
        this.router.navigateByUrl(this.returnUrl);
      },
      error: () => {
        this.saving = false;
        this.errorMessage = 'Unable to save your privacy policy acceptance. Please try again.';
      }
    });
  }

  async decline(): Promise<void> {
    if (!this.isAcceptanceMode || this.saving) {
      return;
    }

    this.saving = true;
    this.privacyPolicyService.decline().subscribe({
      next: async response => {
        alert(response?.message || 'Your account has been deactivated because the privacy policy was declined.');
        this.privacyPolicyService.clearCachedAcceptance();
        await this.ssoService.logout();
      },
      error: async () => {
        alert('Your account could not be updated. You will be signed out.');
        this.privacyPolicyService.clearCachedAcceptance();
        await this.ssoService.logout();
      }
    });
  }
}
